import { HTTP } from "./http";
import { getAuth, onAuthStateChanged } from "firebase/auth";
let API_SERVER = "";

/**
 * Firebase restores the signed-in user asynchronously after page load, so
 * `currentUser` is null for a moment even when a session exists. Wait for
 * the first auth-state emission (bounded) before deciding there's no user.
 */
function firebaseUser(timeoutMs = 4000) {
  const auth = getAuth();
  if (auth.currentUser) return Promise.resolve(auth.currentUser);
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      unsubscribe();
      resolve(auth.currentUser);
    }, timeoutMs);
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      clearTimeout(timer);
      unsubscribe();
      resolve(user);
    });
  });
}

/**
 * Writes to this endpoint are verified server-side (same pattern as the
 * medical service), so every request carries the signed-in user's Firebase
 * ID token. Null when nobody is signed in — the server then answers 401
 * ("sign out and back in").
 */
async function withToken(params) {
  try {
    const user = await firebaseUser();
    const idToken = user ? await user.getIdToken() : null;
    return { ...params, idToken };
  } catch (e) {
    console.warn("PhysicalPerformance: could not get Firebase ID token", e);
    return { ...params, idToken: null };
  }
}
if (window.location.href.indexOf("8080") > -1) {
  API_SERVER =
    "http://www.localhost:8888/back-end-endpoint-player-physical-performance.php";
} else {
  API_SERVER =
    "/personal-training-BE/back-end-endpoint-player-physical-performance.php";
}

const PlayerPhysicalPerformance = {
  async find(params) {
    return HTTP.post(API_SERVER, await withToken(params));
  },

  show(id) {
    return HTTP.get(`users/${id}`);
  },

  async store(post) {
    return HTTP.post(API_SERVER, await withToken(post));
  },

  update(post, id) {
    return HTTP.put(`users/${id}`, post);
  },

  delete(params) {
    return HTTP.delete(API_SERVER, params);
  },

  // ========================================================================
  // BIOBAND ENHANCEMENT METHODS
  // ========================================================================

  /**
   * Save bio age measurement with historical tracking
   * @param {Object} measurementData - Complete measurement data
   * @returns {Promise} - API response
   */
  async saveBioAgeMeasurement(measurementData) {
    return HTTP.post(API_SERVER, await withToken({
      action: "saveBioAgeMeasurement",
      ...measurementData
    }));
  },

  /**
   * Get bio age measurement history for a player
   * @param {number} userId - Player ID
   * @param {number} limit - Max records to return (default 50)
   * @returns {Promise} - API response with measurement history
   */
  getBioAgeHistory(userId, limit = 50) {
    return HTTP.post(API_SERVER, {
      action: "getBioAgeHistory",
      userId,
      limit
    });
  },

  /**
   * Get current bio age results for a specific player
   * @param {number} userId - Player ID
   * @returns {Promise} - API response with bio age results
   */
  getBioAgeResults(userId) {
    return HTTP.post(API_SERVER, {
      action: "getBioAgeResults",
      userId
    });
  },

  /**
   * Get bio age results for all players in a team
   * @param {string} teamId - Team ID
   * @param {number} requestingUserId - Coach/Admin making the request
   * @param {string} userRole - 'Admin', 'Coach', or 'User'
   * @returns {Promise} - API response with squad maturity data
   */
  getBioAgeResultsByTeamId(teamId, requestingUserId, userRole) {
    return HTTP.post(API_SERVER, {
      action: "getBioAgeResultsByTeamId",
      teamId,
      requestingUserId,
      userRole
    });
  },

  /**
   * Get position-based maturity recommendations
   * @param {string} generalPosition - 'Goalkeeper', 'Defender', 'Midfielder', 'Forward'
   * @param {string} maturityCategory - 'Pre-PHV', 'Circa-PHV', 'Post-PHV'
   * @returns {Promise} - API response with recommendations
   */
  getMaturityRecommendations(generalPosition, maturityCategory) {
    return HTTP.post(API_SERVER, {
      action: "getMaturityRecommendations",
      generalPosition,
      maturityCategory
    });
  }
};

export { PlayerPhysicalPerformance };
